import { z } from 'zod';
import { isoDateSchema, optionalText, shortText, slugSchema, urlSchema } from '../common.js';

/**
 * The public wedding site: what guests see, and the RSVP form they answer.
 *
 * Everything here is content the couple writes. Nothing about a guest lives on
 * this object — the public endpoint serves it to anyone who knows the slug, so
 * a single guest name on it would be a guest name on the open internet.
 */

const longText = z.string().trim().max(4000);

/** One of the two or three places a guest has to physically be. */
export const siteEventSchema = z.object({
  /** "Ceremony", "Reception", "Welcome drinks". */
  name: shortText,
  /** Local to the wedding, 24-hour. Not a datetime: the date is the wedding's. */
  startTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM')
    .optional(),
  venueName: optionalText,
  address: optionalText,
  /** A map link, or anything else worth a tap. */
  url: urlSchema.optional(),
  note: optionalText,
});
export type SiteEvent = z.infer<typeof siteEventSchema>;

export const siteFaqSchema = z.object({
  question: shortText,
  answer: longText,
});

export const siteLinkSchema = z.object({
  label: shortText,
  url: urlSchema,
});

export const weddingSiteSchema = z.object({
  /** Over the couple's names. Falls back to the wedding title when empty. */
  headline: optionalText,
  story: longText.optional(),
  events: z.array(siteEventSchema).max(8).default([]),
  travel: longText.optional(),
  dressCode: optionalText,
  registry: z.array(siteLinkSchema).max(10).default([]),
  faq: z.array(siteFaqSchema).max(20).default([]),
  /**
   * After this date the form stops accepting answers. A guest who arrives late
   * is told the date rather than shown a form that silently fails.
   */
  rsvpDeadline: isoDateSchema.optional(),
  /**
   * Offered on the RSVP form. Empty means the couple is not asking, and the
   * meal question disappears rather than showing an empty dropdown.
   */
  mealChoices: z.array(shortText).max(10).default([]),
  songRequests: z.boolean().default(false),
  /** Shown above the form, e.g. "Adults only, with love." */
  rsvpNote: optionalText,
});
export type WeddingSite = z.infer<typeof weddingSiteSchema>;

export const updateSiteSchema = weddingSiteSchema.partial();
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>;

/** Setting the address the site lives at, and whether it answers at all. */
export const publishSiteSchema = z.object({
  slug: slugSchema.optional(),
  sitePublished: z.boolean().optional(),
});

/**
 * What `GET /public/site/{slug}` returns.
 *
 * Deliberately assembled rather than spread from the wedding row: the wedding
 * carries the workspace id, the budget settings and the guest target, none of
 * which is anybody's business.
 */
export const publicSiteSchema = z.object({
  slug: slugSchema,
  title: shortText,
  partnerOneName: optionalText,
  partnerTwoName: optionalText,
  date: isoDateSchema.optional(),
  city: optionalText,
  site: weddingSiteSchema,
  /** Computed from the deadline, so the page never has to know today's date. */
  rsvpOpen: z.boolean(),
  /**
   * The language the page renders in — the couple's, not the reader's.
   *
   * A wedding in Cartagena whose guests mostly speak Spanish must not render in
   * English because one of them opened it on an English phone. The couple's own
   * words are in their language already; the furniture around them should match.
   */
  locale: z.enum(['en', 'es']).default('en'),
});
export type PublicSite = z.infer<typeof publicSiteSchema>;

/** One person's answer, submitted with a household's token. */
export const rsvpAnswerSchema = z.object({
  guestId: z.string().min(1).max(40),
  attending: z.boolean(),
  mealChoice: optionalText,
  dietaryNotes: z.string().trim().max(500).optional(),
});

export type RsvpAnswer = z.infer<typeof rsvpAnswerSchema>;

export const rsvpSubmissionSchema = z.object({
  answers: z.array(rsvpAnswerSchema).min(1).max(20),
  songRequest: z.string().trim().max(500).optional(),
  message: z.string().trim().max(1000).optional(),
});
export type RsvpSubmission = z.infer<typeof rsvpSubmissionSchema>;

/**
 * What the token resolves to: only the household it names, plus the few bits of
 * the couple's own page the form needs to stand on its own.
 *
 * Those extras are here so the RSVP link is a single request. The alternative —
 * fetch the household, read its slug, fetch the site — means a form that cannot
 * render until two round trips have finished, on a phone, at a wedding guest's
 * patience level. Everything added is content the household was already sent.
 */
export const rsvpHouseholdSchema = z.object({
  householdLabel: shortText,
  deadlinePassed: z.boolean(),
  /** For the heading, so the page says whose wedding this is. */
  title: shortText,
  date: isoDateSchema.optional(),
  slug: slugSchema.optional(),
  mealChoices: z.array(shortText).default([]),
  songRequests: z.boolean().default(false),
  rsvpNote: optionalText,
  /** Echoed back so a family can see what they said last time. */
  songRequest: z.string().trim().max(500).optional(),
  /** The couple's language, for the same reason as on the site itself. */
  locale: z.enum(['en', 'es']).default('en'),
  members: z.array(
    z.object({
      guestId: z.string(),
      name: shortText,
      attending: z.boolean().nullable(),
      mealChoice: optionalText,
      dietaryNotes: optionalText,
      ageBand: z.string().optional(),
    }),
  ),
});
export type RsvpHousehold = z.infer<typeof rsvpHouseholdSchema>;
